import {
  Home, Building2, Briefcase, RefreshCw, Warehouse, HardHat, FileSignature, Landmark,
  Factory, HeartPulse, Hotel, Truck, BadgeCheck, Rocket, Building, Compass, Target,
  Eye, Handshake, Layers, TrendingUp, ShieldCheck, Presentation,
} from "lucide-react";

export const CONTACT = {
  phone: "+91 98206 89982",
  phoneHref: "tel:+919820689982",
  landline: "+91 22 6897 6160",
  landlineHref: "tel:+912268976160",
  email: "connect@lendxpro.com",
  emailHref: "mailto:connect@lendxpro.com",
  website: "www.lendxpro.com",
  websiteHref: "https://www.lendxpro.com",
  whatsapp:
    "https://wa.me/919820689982?text=Hello%20LendX%2C%20I%20am%20interested%20in%20exploring%20financing%20options.%20Please%20contact%20me.",
  hours: "Mon – Fri · 10:30 AM – 9:00 PM · Sat · 11:00 AM – 5:30 PM",
  address:
    "Akshar Business Park, Unit No. Y1111, First Floor, Sector 25, Vashi-Turbhe Road, Vashi, Navi Mumbai 400703",
  mapsUrl: "https://share.google/kAhTnJlRPyGz1rKw8",
  mapsEmbed:
    "https://www.google.com/maps?q=LendX,+Akshar+Business+Park,+Sector+25,+Vashi,+Navi+Mumbai+400703&output=embed",
};

export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Loan Solutions", path: "/solutions" },
  { label: "Business Finance", path: "/business-finance" },
  { label: "Property Finance", path: "/property-finance" },
  { label: "Why LendX", path: "/why-lendx" },
  { label: "Contact Us", path: "/contact" },
];

export const REQUIREMENTS = [
  "Home Loan", "Loan Against Property", "Business Loan", "Working Capital Finance",
  "Commercial Property Finance", "Construction Finance", "Lease Rental Discounting",
  "Corporate Finance", "Debt & Equity Capital Structuring", "Wealth Growth & Portfolio Diversification",
  "Risk Mitigation & Financial Resilience", "M&A Advisory & Execution", "SME IPO Advisory", "Other",
];

export const SERVICES = [
  {
    slug: "home-loan",
    title: "Home Loan",
    icon: Home,
    category: "property",
    short: "Finance solutions for purchasing residential properties.",
    headline: "Own Your Home with the Right Financing Structure",
    intro:
      "Buying a home is one of the most significant financial decisions you will make. LendX helps you understand your eligibility, compare suitable financing approaches, and navigate the process with clarity — from assessment to documentation coordination.",
    suitableFor: [
      "Salaried professionals planning their first home",
      "Self-employed individuals and business owners",
      "Buyers of under-construction or resale properties",
      "Borrowers exploring a balance transfer or top-up",
    ],
    benefits: [
      "Structured eligibility assessment across financing options",
      "Guidance on tenure, repayment structure, and down payment",
      "Support with balance transfer and top-up evaluations",
      "End-to-end documentation and application coordination",
    ],
    options: [
      "Ready-to-move and resale home purchase finance",
      "Under-construction property finance",
      "Plot purchase and construction finance",
      "Home loan balance transfer with top-up",
      "Home improvement and extension finance",
    ],
    documents: [
      "KYC documents (identity and address proof)",
      "Income proof — salary slips / ITR and financials",
      "Bank statements (6–12 months)",
      "Property documents and agreement copy",
      "Existing loan statements, if any",
    ],
    faqs: [
      {
        q: "Does LendX provide home loans directly?",
        a: "No. LendX is a financial advisory and facilitation platform. We help you explore suitable financing options and coordinate the process; approval and disbursal are subject to the respective financial institution's policies.",
      },
      {
        q: "How much home loan am I eligible for?",
        a: "Eligibility depends on your income profile, existing obligations, credit history, and the property value. Our team assesses your profile and helps you understand a realistic financing range.",
      },
      {
        q: "Can I transfer my existing home loan?",
        a: "Balance transfer may be an option depending on your repayment track record and the terms offered by other institutions. We help you evaluate whether a transfer makes financial sense for you.",
      },
    ],
  },
  {
    slug: "loan-against-property",
    title: "Loan Against Property",
    icon: Building2,
    category: "property",
    short: "Unlock the value of your residential or commercial property.",
    headline: "Unlock the Value Locked in Your Property",
    intro:
      "A loan against property lets you leverage your residential or commercial asset to access substantial funding — for business expansion, working capital, or other significant requirements — while retaining ownership of the property.",
    suitableFor: [
      "Business owners seeking growth capital",
      "Professionals funding large personal or business goals",
      "Property owners with clear, marketable titles",
      "Borrowers consolidating higher-cost obligations",
    ],
    benefits: [
      "Structured assessment of property value and eligibility",
      "Funding options against residential or commercial assets",
      "Guidance on tenure and repayment structuring",
      "Coordination through valuation, legal, and documentation stages",
    ],
    options: [
      "LAP against self-occupied residential property",
      "LAP against commercial property",
      "LAP against rented / leased property",
      "Top-up on existing property loans",
    ],
    documents: [
      "KYC documents",
      "Property title documents and chain of agreements",
      "Income proof and financial statements",
      "Bank statements (6–12 months)",
      "Existing loan details, if applicable",
    ],
    faqs: [
      {
        q: "What types of property can be funded?",
        a: "Residential, commercial, and in some cases industrial properties with clear, marketable titles are generally considered, subject to each institution's policies.",
      },
      {
        q: "How much funding can I expect against my property?",
        a: "Funding typically depends on the property's market value, its type, and your income profile. We help you arrive at a realistic estimate after assessment.",
      },
      {
        q: "Can I continue using my property?",
        a: "Yes. In a loan against property, you retain ownership and usage of the property while it remains mortgaged with the financing institution.",
      },
    ],
  },
  {
    slug: "business-loan",
    title: "Business Loan",
    icon: Briefcase,
    category: "business",
    short: "Flexible financing solutions designed around business requirements.",
    headline: "Flexible Capital for Ambitious Businesses",
    intro:
      "Whether you are expanding capacity, entering new markets, or strengthening your balance sheet, LendX helps you structure business financing aligned to your cash flows, vintage, and growth plans.",
    suitableFor: [
      "SMEs and MSMEs planning expansion",
      "Proprietorships, partnerships, LLPs, and private limited companies",
      "Professionals scaling their practice",
      "Businesses consolidating expensive short-term debt",
    ],
    benefits: [
      "Financing structured around your business cash flows",
      "Access to secured and unsecured funding approaches",
      "Guidance on improving creditworthiness and presentation",
      "Coordination across assessment, sanction, and documentation",
    ],
    options: [
      "Unsecured business loans",
      "Secured business loans against assets or receivables",
      "Machinery and equipment finance",
      "Business expansion and term loans",
    ],
    documents: [
      "KYC of promoters and business",
      "Business registration and vintage proof",
      "ITR and financial statements (2–3 years)",
      "Bank statements (12 months)",
      "GST returns, where applicable",
    ],
    faqs: [
      {
        q: "What turnover is needed for a business loan?",
        a: "Requirements vary across institutions and depend on profitability, banking conduct, and credit history. We assess your financials and suggest suitable financing approaches.",
      },
      {
        q: "Are unsecured business loans possible?",
        a: "Yes, certain institutions offer unsecured business loans based on business vintage, turnover, banking, and credit profile. Terms differ by lender policy.",
      },
      {
        q: "How long does the process take?",
        a: "Timelines depend on the complexity of the requirement, documentation readiness, and the institution's internal process. We coordinate to keep the process moving efficiently.",
      },
    ],
  },
  {
    slug: "working-capital-finance",
    title: "Working Capital Finance",
    icon: RefreshCw,
    category: "business",
    short: "Funding support for day-to-day business operations and growth.",
    headline: "Keep Your Operations Moving, Always",
    intro:
      "Working capital gaps should never slow down a healthy business. LendX helps you explore funding structures that smooth out receivable cycles, fund inventory, and support seasonal demand — so operations never miss a beat.",
    suitableFor: [
      "Trading and distribution businesses",
      "Manufacturers with long production cycles",
      "Service businesses with extended receivables",
      "Seasonal businesses managing demand spikes",
    ],
    benefits: [
      "Structures aligned to your operating cycle",
      "Assessment of fund-based and non-fund-based requirements",
      "Guidance on optimizing receivables and inventory funding",
      "Ongoing review support as limits and needs evolve",
    ],
    options: [
      "Cash credit and overdraft facilities",
      "Invoice and receivable discounting",
      "Inventory and stock finance",
      "Trade finance — letters of credit and bank guarantees",
    ],
    documents: [
      "KYC and business registration documents",
      "Financial statements and projections",
      "Bank statements (12 months)",
      "Debtor and creditor ageing, stock statements",
      "GST returns and turnover details",
    ],
    faqs: [
      {
        q: "What is the difference between cash credit and overdraft?",
        a: "Both are revolving working capital facilities. Cash credit is typically against inventory and receivables, while overdrafts may be against other securities. Suitability depends on your business model.",
      },
      {
        q: "Can service businesses get working capital finance?",
        a: "Yes. Service businesses are commonly assessed on receivables, contracts, and cash flows rather than stock. We help structure the right approach.",
      },
      {
        q: "How is the working capital limit decided?",
        a: "Limits are generally linked to your operating cycle, turnover, and margins, assessed per each institution's norms.",
      },
    ],
  },
  {
    slug: "commercial-property-finance",
    title: "Commercial Property Finance",
    icon: Warehouse,
    category: "property",
    short: "Structured financing for offices, shops, warehouses, and commercial assets.",
    headline: "Structured Finance for Commercial Assets",
    intro:
      "Acquiring commercial real estate demands a financing structure that respects both the asset and the cash flows behind it. LendX helps investors and businesses explore funding for offices, retail, warehouses, and other income-generating commercial assets.",
    suitableFor: [
      "Businesses purchasing their own office or premises",
      "Investors acquiring income-generating commercial assets",
      "Developers refinancing completed commercial projects",
      "Enterprises funding warehouses or industrial sheds",
    ],
    benefits: [
      "Assessment of asset value, title, and income potential",
      "Structures aligned to rental or business cash flows",
      "Coordination across legal, valuation, and technical stages",
      "Refinancing and top-up evaluation support",
    ],
    options: [
      "Purchase of office spaces and retail units",
      "Warehouse and industrial property finance",
      "Refinance of owned commercial assets",
      "Lease-backed commercial property finance",
    ],
    documents: [
      "KYC of borrowers and guarantors",
      "Property title and approval documents",
      "Financial statements and income proof",
      "Bank statements (12 months)",
      "Lease agreements, where the asset is tenanted",
    ],
    faqs: [
      {
        q: "Can I get finance for an under-construction commercial property?",
        a: "Certain institutions consider under-construction commercial assets based on the developer's profile and project approvals. We help evaluate feasibility.",
      },
      {
        q: "Is rental income considered for eligibility?",
        a: "Yes, existing or expected rental income from the asset is often factored into eligibility, subject to documentation and lender policy.",
      },
      {
        q: "What loan-to-value can I expect?",
        a: "Funding ratios vary by asset type, location, and borrower profile. We help you understand realistic expectations after assessment.",
      },
    ],
  },
  {
    slug: "construction-finance",
    title: "Construction Finance",
    icon: HardHat,
    category: "business",
    short: "Funding solutions for construction and real estate projects.",
    headline: "Funding Built for Real Estate Development",
    intro:
      "Real estate development moves at the speed of its funding. LendX works with developers to explore construction finance structures aligned to project milestones, approvals, and sales velocity.",
    suitableFor: [
      "Developers launching residential projects",
      "Builders of commercial and mixed-use developments",
      "Landowners entering joint development arrangements",
      "Developers refinancing ongoing projects",
    ],
    benefits: [
      "Milestone-linked disbursement structuring",
      "Assessment of project approvals, RERA status, and sales plan",
      "Coordination with legal and technical diligence",
      "Inventory funding and refinancing evaluations",
    ],
    options: [
      "Project construction finance",
      "Inventory / unsold stock funding",
      "Land and approval-stage funding evaluations",
      "Refinancing of ongoing projects",
    ],
    documents: [
      "Developer entity KYC and track record",
      "Project approvals, title, and RERA registration",
      "Project cost, cash flow, and sales projections",
      "Existing project loan details, if any",
      "Promoter financials and banking history",
    ],
    faqs: [
      {
        q: "At what project stage can construction finance be raised?",
        a: "Institutions typically prefer projects with key approvals in place. Early-stage requirements are evaluated on the promoter's track record and project fundamentals.",
      },
      {
        q: "How are disbursements structured?",
        a: "Disbursements are usually linked to construction milestones and project progress, verified through technical assessments.",
      },
      {
        q: "Does LendX fund projects directly?",
        a: "No. LendX is an advisory and facilitation platform. We help structure the requirement and coordinate with suitable financial institutions.",
      },
    ],
  },
  {
    slug: "lease-rental-discounting",
    title: "Lease Rental Discounting",
    icon: FileSignature,
    category: "property",
    short: "Explore funding options against future rental income from leased properties.",
    headline: "Monetize Your Future Rental Income",
    intro:
      "If you own a leased commercial property, your future rental income can work for you today. LendX helps property owners explore lease rental discounting structures that convert stable rent flows into upfront funding.",
    suitableFor: [
      "Owners of commercially leased office or retail assets",
      "Landlords with corporate or institutional tenants",
      "Investors seeking liquidity without selling the asset",
      "Businesses unlocking capital from owned, leased premises",
    ],
    benefits: [
      "Funding sized on rental income and lease strength",
      "Tenant profile and lease tenor assessment",
      "Structures that preserve asset ownership",
      "Coordination across legal and documentation stages",
    ],
    options: [
      "LRD against commercial office leases",
      "LRD against retail and high-street assets",
      "LRD with top-up on property value appreciation",
      "Refinancing of existing LRD facilities",
    ],
    documents: [
      "KYC and ownership documents",
      "Registered lease agreement",
      "Rental receipts and bank statements",
      "Tenant profile and financials, where available",
      "Property title documents",
    ],
    faqs: [
      {
        q: "How much funding can be raised against rent?",
        a: "Funding is typically linked to the net present value of rentals over the lease tenor, subject to tenant quality and lender norms.",
      },
      {
        q: "Does the tenant need to be a corporate?",
        a: "Strong corporate or institutional tenants generally improve funding terms, but profiles vary across institutions.",
      },
      {
        q: "Will my tenant be involved in the process?",
        a: "Rent is usually routed through an escrow mechanism, which involves limited coordination with the tenant, depending on the structure.",
      },
    ],
  },
  {
    slug: "corporate-finance",
    title: "Corporate Finance",
    icon: Landmark,
    category: "business",
    short: "Customized financing structures for growing businesses and corporates.",
    headline: "Structured Capital for Growing Corporates",
    intro:
      "As businesses scale, financing stops being a product and becomes a strategy. LendX supports corporates with structured financing approaches — across debt, refinancing, and capital structuring — aligned to long-term objectives.",
    suitableFor: [
      "Mid-market companies planning expansion or capex",
      "Corporates restructuring or refinancing existing debt",
      "Groups funding acquisitions or new ventures",
      "Promoters seeking structured capital solutions",
    ],
    benefits: [
      "Requirement-first, structure-led financing approach",
      "Access to a broad institutional funding ecosystem",
      "Support across diligence, term sheets, and documentation",
      "Relationship-led ongoing capital advisory",
    ],
    options: [
      "Term loans and project-linked facilities",
      "Debt restructuring and refinancing",
      "Structured and mezzanine debt evaluations",
      "Acquisition and special-situation financing",
    ],
    documents: [
      "Corporate KYC and constitutional documents",
      "Audited financials (3 years) and projections",
      "Existing debt schedule and banking details",
      "Transaction rationale and funding plan",
      "Security and collateral details",
    ],
    faqs: [
      {
        q: "What ticket sizes does corporate finance cover?",
        a: "Corporate financing requirements vary widely. Suitability depends on the company's financials, the transaction structure, and the security offered.",
      },
      {
        q: "Can LendX help refinance existing debt?",
        a: "Yes. We help evaluate refinancing and restructuring options aimed at improving terms, tenure, or overall capital efficiency.",
      },
      {
        q: "Do you work with startups?",
        a: "We work with businesses that have demonstrable financials and assets. Early-stage startups may be better served once revenue and banking history are established.",
      },
    ],
  },
];

export const ADVISORY_SERVICES = [
  {
    slug: "debt-equity-capital-structuring",
    title: "Debt & Equity Capital Structuring",
    icon: Layers,
    category: "advisory",
    short: "Optimal capital structures balancing debt, equity, and hybrid instruments.",
    headline: "Engineer the Right Balance of Debt and Equity",
    intro:
      "The right capital structure lowers your cost of capital and expands strategic freedom. LendX advises promoters and corporates on structuring debt, equity, and hybrid capital aligned to growth plans, cash flows, and ownership objectives.",
    suitableFor: [
      "Corporates optimizing their cost of capital",
      "Promoters balancing growth funding with ownership dilution",
      "Companies preparing for institutional fundraises",
      "Groups consolidating or re-leveraging balance sheets",
    ],
    benefits: [
      "Capital structure diagnostics and benchmarking",
      "Debt-equity mix modelling aligned to cash flows",
      "Access to institutional debt and equity ecosystems",
      "Support through term sheets, diligence, and closure",
    ],
    options: [
      "Structured debt and mezzanine instruments",
      "Equity and quasi-equity fundraise advisory",
      "Convertible and hybrid instrument structuring",
      "Balance sheet re-leveraging and recapitalization",
    ],
    documents: [
      "Corporate KYC and constitutional documents",
      "Audited financials (3 years) and projections",
      "Existing capital and debt structure details",
      "Business plan and fund utilization rationale",
      "Shareholding pattern and promoter details",
    ],
    faqs: [
      {
        q: "When should a company re-look at its capital structure?",
        a: "Typically ahead of major expansion, acquisitions, refinancing cycles, or fundraises — or when the current structure constrains cash flows and growth. We assess and recommend the right timing and mix.",
      },
      {
        q: "Does LendX invest directly?",
        a: "No. LendX is an advisory and facilitation platform. We structure the requirement and coordinate with suitable institutional capital providers.",
      },
      {
        q: "Can hybrid instruments reduce dilution?",
        a: "Yes. Instruments like convertibles or structured debt can defer or reduce equity dilution while providing growth capital, subject to suitability and investor appetite.",
      },
    ],
  },
  {
    slug: "wealth-growth-portfolio-diversification",
    title: "Wealth Growth & Portfolio Diversification",
    icon: TrendingUp,
    category: "advisory",
    short: "Strategic guidance to grow, diversify, and preserve wealth across asset classes.",
    headline: "Grow and Diversify Wealth with Discipline",
    intro:
      "Sustainable wealth is built on structure, not chance. LendX guides promoters, professionals, and families on diversifying across asset classes — balancing growth, liquidity, and preservation as financial goals evolve.",
    suitableFor: [
      "Promoters with concentrated business wealth",
      "Professionals and HNIs building long-term portfolios",
      "Families planning intergenerational wealth transfer",
      "Investors seeking structured diversification",
    ],
    benefits: [
      "Goal-based wealth strategy and asset allocation guidance",
      "Diversification across real estate, debt, and alternative assets",
      "Liquidity planning aligned to business and personal goals",
      "Periodic portfolio review and rebalancing support",
    ],
    options: [
      "Asset allocation and diversification strategy",
      "Real estate and yield-asset advisory",
      "Fixed income and structured product evaluation",
      "Alternative investment opportunity assessment",
    ],
    documents: [
      "KYC documents",
      "Current portfolio and asset summary",
      "Financial goals and liquidity requirements",
      "Risk appetite and horizon preferences",
      "Existing advisor or product statements, if any",
    ],
    faqs: [
      {
        q: "Is this a portfolio management service (PMS)?",
        a: "No. LendX provides advisory guidance and facilitates access to suitable opportunities. Investment decisions and product execution rest with the client and respective regulated providers.",
      },
      {
        q: "What is the minimum portfolio size?",
        a: "Engagements are customized. The approach depends on your goals and asset base rather than a fixed threshold — we assess suitability in the first conversation.",
      },
      {
        q: "How often is the strategy reviewed?",
        a: "We recommend structured reviews at least annually, or whenever significant business, market, or personal changes occur.",
      },
    ],
  },
  {
    slug: "risk-mitigation-financial-resilience",
    title: "Risk Mitigation & Financial Resilience",
    icon: ShieldCheck,
    category: "advisory",
    short: "Structured strategies to protect cash flows, assets, and long-term stability.",
    headline: "Build Resilience Before You Need It",
    intro:
      "Strong businesses are stress-tested by design. LendX helps businesses and promoters identify financial vulnerabilities — leverage, concentration, liquidity, and contingency gaps — and build structured resilience against them.",
    suitableFor: [
      "Businesses with concentrated customer or lender exposure",
      "Promoters with personal guarantees and cross-collateralization",
      "Companies exposed to cyclical or seasonal cash flows",
      "Groups planning contingency and succession structures",
    ],
    benefits: [
      "Financial risk diagnostics across leverage and liquidity",
      "Debt maturity and refinancing risk assessment",
      "Contingency funding line and buffer structuring",
      "Guarantee, collateral, and exposure rationalization",
    ],
    options: [
      "Liquidity buffer and contingency line structuring",
      "Debt profile de-risking and maturity laddering",
      "Collateral and guarantee optimization",
      "Stress scenario planning and mitigation road-maps",
    ],
    documents: [
      "Business and promoter KYC",
      "Debt schedule with security and guarantee details",
      "Cash flow statements and projections",
      "Customer and revenue concentration summary",
      "Existing insurance and hedging arrangements, if any",
    ],
    faqs: [
      {
        q: "How is this different from insurance?",
        a: "Insurance transfers specific risks. Financial resilience is broader — structuring liquidity, leverage, and obligations so the business withstands shocks. The two often work together.",
      },
      {
        q: "When should a business do a risk review?",
        a: "Ideally annually, and always before major expansion, new leverage, or macro uncertainty. Early diagnosis keeps options open and costs low.",
      },
      {
        q: "Can personal guarantee exposure be reduced?",
        a: "In many cases, yes — through refinancing, collateral substitution, or structure changes over time, subject to lender policies.",
      },
    ],
  },
  {
    slug: "ma-advisory-execution",
    title: "End-to-End M&A Advisory & Execution",
    icon: Handshake,
    category: "advisory",
    short: "Buy-side and sell-side advisory from strategy through closure.",
    headline: "Navigate Mergers & Acquisitions with Confidence",
    intro:
      "M&A transactions reward preparation and disciplined execution. LendX supports promoters and corporates through the full deal lifecycle — target identification, valuation, structuring, diligence coordination, and closure.",
    suitableFor: [
      "Companies acquiring capabilities, markets, or capacity",
      "Promoters evaluating a full or partial exit",
      "Groups consolidating fragmented businesses",
      "Corporates divesting non-core assets",
    ],
    benefits: [
      "Buy-side and sell-side process management",
      "Valuation guidance and deal structuring",
      "Coordination of legal, financial, and tax diligence",
      "Negotiation support through term sheet to closure",
    ],
    options: [
      "Sell-side mandates and exit advisory",
      "Buy-side target search and evaluation",
      "Merger and consolidation structuring",
      "Divestiture and carve-out advisory",
    ],
    documents: [
      "Corporate KYC and shareholding pattern",
      "Audited financials (3 years) and projections",
      "Business overview and transaction rationale",
      "Existing debt and encumbrance details",
      "Key contracts and licences summary, at diligence stage",
    ],
    faqs: [
      {
        q: "How long does an M&A transaction take?",
        a: "Typical transactions run 6–12 months depending on readiness, complexity, and counterparties. Preparation quality is the biggest driver of speed.",
      },
      {
        q: "Does LendX work on both buy-side and sell-side?",
        a: "Yes, though never on both sides of the same transaction. Each mandate is exclusive with clear conflict checks.",
      },
      {
        q: "How is confidentiality maintained?",
        a: "Through staged information release, NDAs before data sharing, and anonymized initial outreach — standard practice in every mandate.",
      },
    ],
  },
  {
    slug: "sme-ipo-advisory",
    title: "SME IPO Advisory",
    icon: Presentation,
    category: "advisory",
    short: "End-to-end guidance for SMEs preparing to list on SME exchange platforms.",
    headline: "Take Your SME Public, the Prepared Way",
    intro:
      "An SME IPO can unlock capital, credibility, and liquidity — if the groundwork is right. LendX guides promoters through IPO readiness, intermediary selection, and the listing journey on NSE Emerge and BSE SME platforms.",
    suitableFor: [
      "Profitable SMEs meeting exchange eligibility norms",
      "Promoters seeking growth capital with public currency",
      "Family businesses formalizing governance for scale",
      "Companies planning migration to the main board over time",
    ],
    benefits: [
      "IPO readiness diagnostics and gap remediation",
      "Financial restatement and governance preparation guidance",
      "Merchant banker and intermediary selection support",
      "Coordination through DRHP, marketing, and listing",
    ],
    options: [
      "IPO readiness assessment and road-map",
      "Pre-IPO structuring and capital planning",
      "NSE Emerge / BSE SME listing process advisory",
      "Post-listing compliance and investor relations guidance",
    ],
    documents: [
      "Corporate KYC and constitutional documents",
      "Audited financials (3 years) with restatement readiness",
      "Shareholding pattern and promoter details",
      "Business plan and use-of-proceeds rationale",
      "Existing debt, litigation, and compliance summary",
    ],
    faqs: [
      {
        q: "What are the basic eligibility norms for an SME IPO?",
        a: "Exchanges prescribe norms around net worth, track record, and profitability. We run a readiness diagnostic against current NSE Emerge and BSE SME criteria for your company.",
      },
      {
        q: "How long does an SME IPO take?",
        a: "From readiness to listing typically takes 6–9 months, depending on financial restatement, governance preparation, and regulatory timelines.",
      },
      {
        q: "Is LendX a merchant banker?",
        a: "No. LendX acts as the promoter's advisor — preparing the company, selecting the right intermediaries, and coordinating the process end to end.",
      },
    ],
  },
];

export const ALL_SERVICES = [...SERVICES, ...ADVISORY_SERVICES];

export const WHY_LENDX = [
  {
    title: "Expert Financial Guidance",
    text: "Understand suitable financing structures based on your requirements.",
  },
  {
    title: "Tailored Solutions",
    text: "Explore financing options based on your financial profile and objectives.",
  },
  {
    title: "Strong Market Understanding",
    text: "Professional understanding of lending products, structures, and requirements.",
  },
  {
    title: "End-to-End Support",
    text: "Support throughout the assessment, application, and documentation process.",
  },
  {
    title: "Relationship-Based Approach",
    text: "Long-term financial guidance beyond a single transaction.",
  },
];

export const STEPS = [
  { title: "Tell Us Your Requirement", text: "Share your funding requirement and financial objective." },
  { title: "Financial Assessment", text: "We assess your requirement and understand your financing needs." },
  { title: "Solution Structuring", text: "We help identify suitable financing approaches and options." },
  { title: "Documentation Support", text: "Guidance through the application and documentation process." },
  { title: "Funding Coordination", text: "Professional coordination and support throughout the financing process." },
];

export const INDUSTRIES = [
  { name: "Real Estate", icon: Building },
  { name: "Manufacturing", icon: Factory },
  { name: "Trading", icon: RefreshCw },
  { name: "Healthcare", icon: HeartPulse },
  { name: "Hospitality", icon: Hotel },
  { name: "Logistics", icon: Truck },
  { name: "Professional Services", icon: BadgeCheck },
  { name: "Startups & SMEs", icon: Rocket },
  { name: "Corporates", icon: Landmark },
];

export const ABOUT_PILLARS = [
  {
    icon: Eye,
    title: "Our Vision",
    text: "To become a trusted financial solutions partner by helping clients make informed and strategic financing decisions.",
  },
  {
    icon: Target,
    title: "Our Mission",
    text: "To simplify complex financing requirements through professional guidance, suitable solution structuring, and personalized support.",
  },
  {
    icon: Compass,
    title: "Our Approach",
    text: "Understand. Assess. Structure. Support.",
  },
];

export const LENDER_CATEGORIES = [
  "Scheduled Commercial Banks",
  "Non-Banking Financial Companies",
  "Housing Finance Companies",
  "Private Credit & Debt Funds",
  "Alternative Investment Funds",
  "Co-operative & Regional Banks",
  "International Financial Institutions",
  "Fintech Lending Platforms",
];

export const GOOGLE_REVIEWS = {
  rating: "5.0",
  url: "https://www.google.com/maps/place/LendX/data=!4m2!3m1!1s0x0:0xf542e76e8266f834",
  reviews: [
    {
      name: "Karan Garud",
      text: "Clear communication and fast turnaround. The team kept me informed at every stage and made the entire process effortless.",
    },
    {
      name: "Ajeet Agnihotri",
      text: "Quick, transparent, and very professional service. LendX structured exactly what my business needed without any hidden surprises.",
    },
    {
      name: "Rakhee Sahasrabudhe",
      text: "Had a great experience with LendX. Genuine advisory, honest guidance, and support that went well beyond a single transaction.",
    },
  ],
};

export const DISCLAIMER =
  "LendX acts as a financial advisory and facilitation platform and is not a bank or direct lender. Loan approval, interest rates, terms, and disbursement are subject to the policies, eligibility criteria, and final approval of the respective financial institution.";

export const HANDSHAKE_ICON = Handshake;
